import { useMemo, useState } from 'react';
import { Combobox, GetItemsProps } from 'medere-ui-framework';
import { Button } from 'primereact/button';
import { Column } from 'primereact/column';
import { DataTable, DataTableExpandedRows, DataTableValueArray } from 'primereact/datatable';
import { InputText } from 'primereact/inputtext';
import { ProgressSpinner } from 'primereact/progressspinner';
import { getAllCompanies } from '../../servicesHooks/Accounting/useAccountingService';
import { getProfessionals } from '../../servicesHooks/Budget/useBudgetService';
import { createLiquidation, getPendingRetentionSummary } from '../../servicesHooks/Honorarium/useHonorariumLiquidationService';
import { getPendingProfessionalSummary } from '../../servicesHooks/Honorarium/useHonorariumPreLiquidationService';
import { parseApiError } from '../../services/apiError';
import { HonorariumPendingProfessionalSummaryDTO } from '../../dto/HonorariumPreLiquidationDTO';
import { ComboDataDTO } from '../../dto/ComboDataDTO';
import { LIQUIDATION_ERROR_CODES, PendingRetentionSummaryDTO } from '../../dto/HonorariumLiquidationDTO';
import { formatMoney, sumMoney } from '../../utils/money';
import useNotify from '../../hooks/useNotify';
import FieldError from '../../components/FieldError/FieldError';
import PendingPreLiquidationItems from './PendingPreLiquidationItems';
import SignedAmount from '../../components/HonorariumMovement/SignedAmount';

/** Opción del combo de entidad facturadora (getAllCompanies). */
interface BillingEntityOption {
    company: number;
    description: string;
}

/**
 * Profesional con algo pendiente de liquidar: pre-liquidaciones de la entidad, retenciones por facturación propia o
 * las dos cosas. El que factura todo por su cuenta entra solo con sus retenciones.
 */
interface PendingProfessionalRow {
    professional: number;
    professionalName: string;
    preLiqCount: number;
    itemCount: number;
    professionalAmount: number;
    retentionTotal: number;
    retentionLineCount: number;
}

/** Une los pendientes de la entidad con las retenciones pendientes a su favor (si es la que retiene), por profesional. */
function pendingRows(summaries: HonorariumPendingProfessionalSummaryDTO[],
                     retentions: PendingRetentionSummaryDTO[]): PendingProfessionalRow[] {
    const retentionByProfessional = new Map(retentions.map(r => [r.professional, r]));
    const rows: PendingProfessionalRow[] = summaries.map(s => ({
        professional: s.professional,
        professionalName: s.professionalName,
        preLiqCount: s.preLiqCount,
        itemCount: s.itemCount,
        professionalAmount: s.professionalAmount,
        retentionTotal: retentionByProfessional.get(s.professional)?.retentionTotal ?? 0,
        retentionLineCount: retentionByProfessional.get(s.professional)?.lineCount ?? 0,
    }));
    const withItems = new Set(summaries.map(s => s.professional));
    retentions.filter(r => !withItems.has(r.professional)).forEach(r => rows.push({
        professional: r.professional,
        professionalName: r.professionalName,
        preLiqCount: 0,
        itemCount: 0,
        professionalAmount: 0,
        retentionTotal: r.retentionTotal,
        retentionLineCount: r.lineCount,
    }));
    return rows;
}

/** Ítems que ya no se pueden liquidar: otra persona los liquidó o eliminó mientras se armaba la selección. */
const STALE_SELECTION_CODES: string[] = [LIQUIDATION_ERROR_CODES.PRELIQUIDATION_ITEMS_UNAVAILABLE, 'NO_PENDING_ITEMS'];

interface NewLiquidationPanelProps {
    onBack: () => void;
    /** Recibe el id de la liquidación creada, para abrir su detalle. */
    onCreated: (liquidationId: number) => void;
}

/**
 * Nueva liquidación (página, DS §3.7): se buscan los profesionales con pre-liquidaciones pendientes de una entidad o
 * con retenciones por facturación propia pendientes, se eligen (con "seleccionar todos" en la cabecera) y se crea la
 * liquidación abierta. Las retenciones de cada profesional elegido entran solas.
 */
export default function NewLiquidationPanel({ onBack, onCreated }: NewLiquidationPanelProps) {
    const notify = useNotify();

    const [company, setCompany] = useState<BillingEntityOption | null>(null);
    const [professional, setProfessional] = useState<ComboDataDTO | null>(null);
    const [searchedCompany, setSearchedCompany] = useState<BillingEntityOption | null>(null);
    const [summaries, setSummaries] = useState<PendingProfessionalRow[]>([]);
    const [loading, setLoading] = useState(false);
    const [selected, setSelected] = useState<PendingProfessionalRow[]>([]);
    const [expandedRows, setExpandedRows] = useState<DataTableExpandedRows | DataTableValueArray>({});
    const [description, setDescription] = useState('');
    const [companyError, setCompanyError] = useState<string | null>(null);
    const [selectionError, setSelectionError] = useState<string | null>(null);
    const [creating, setCreating] = useState(false);

    // El filtro de profesional se aplica sobre la lista completa de la entidad: no está paginada
    const visibleSummaries = useMemo(() => (professional?.id
        ? summaries.filter(s => s.professional === professional.id)
        : summaries), [summaries, professional]);
    const selectedTotal = useMemo(() => sumMoney(selected.map(s => s.professionalAmount)), [selected]);
    const selectedRetentions = useMemo(() => sumMoney(selected.map(s => s.retentionTotal)), [selected]);

    // Otra entidad: lo buscado y lo elegido ya no corresponden (si no, se crearía para la entidad equivocada)
    function handleCompanyChange(value: BillingEntityOption | null) {
        setCompany(value);
        setCompanyError(null);
        setSearchedCompany(null);
        setSummaries([]);
        setSelected([]);
        setExpandedRows({});
    }

    // Lo elegido que queda oculto por el filtro se deja de elegir: el total tiene que coincidir con lo que se ve
    function handleProfessionalChange(value: ComboDataDTO | null) {
        setProfessional(value);
        if (value?.id) setSelected(current => current.filter(s => s.professional === value.id));
    }

    async function search(target: BillingEntityOption) {
        setLoading(true);
        setSelected([]);
        setExpandedRows({});
        setSelectionError(null);
        try {
            const [pending, retentions] = await Promise.all([
                getPendingProfessionalSummary(target.company),
                getPendingRetentionSummary(target.company),
            ]);
            setSummaries(pendingRows(pending ?? [], retentions ?? []));
            setSearchedCompany(target);
        } catch (error: unknown) {
            parseApiError(error); // loguea errorCode y path para cruzarlo con el backend
            notify.error('No se pudieron cargar los profesionales con pendientes. Intentá de nuevo.');
        } finally {
            setLoading(false);
        }
    }

    function handleSearch() {
        if (!company) {
            setCompanyError('Seleccioná la entidad facturadora');
            return;
        }
        search(company);
    }

    async function handleCreate() {
        if (!searchedCompany) return;
        if (selected.length === 0) {
            setSelectionError('Seleccioná al menos un profesional');
            return;
        }
        setCreating(true);
        try {
            const liquidation = await createLiquidation({
                billingEntityId: searchedCompany.company,
                description,
                professionalIds: selected.map(s => s.professional),
            });
            notify.success(`Liquidación N° ${liquidation.liquidationNumber} creada`);
            onCreated(liquidation.honorariumLiquidation);
        } catch (error: unknown) {
            const apiError = parseApiError(error);
            if (STALE_SELECTION_CODES.includes(apiError.errorCode)) {
                notify.error(apiError.message);
                search(searchedCompany);
                return;
            }
            const isBusinessError = apiError.status >= 400 && apiError.status < 500;
            notify.error(isBusinessError ? apiError.message : 'No se pudo crear la liquidación. Intentá de nuevo.');
        } finally {
            setCreating(false);
        }
    }

    return (
        <div className="new-liquidation">
            <div>
                <Button text icon="pi pi-angle-double-left" label="Volver" className="md-back-link" onClick={onBack} />
            </div>
            <h1 className="md-page-title new-liquidation__title">Nueva liquidación</h1>

            <div className="md-filters">
                <div>
                    <Combobox
                        label="Entidad facturadora *"
                        getItems={(p: GetItemsProps) => getAllCompanies(p)}
                        optionLabel="description"
                        value={company}
                        setValue={handleCompanyChange}
                        placeholder="Seleccionar"
                        error={!!companyError}
                        style={{ width: '240px' }}
                    />
                    <FieldError id="new-liquidation-company-error" message={companyError} />
                </div>
                <Combobox
                    label="Profesional"
                    getItems={(p: GetItemsProps) => getProfessionals(p)}
                    optionLabel="name"
                    value={professional}
                    setValue={handleProfessionalChange}
                    placeholder="Todos"
                    style={{ width: '240px' }}
                />
                <Button
                    outlined
                    className="md-ghost md-filter-action"
                    icon="pi pi-search"
                    label="Buscar"
                    onClick={handleSearch}
                />
            </div>

            {loading && (
                <div className="md-loading" role="status">
                    <ProgressSpinner strokeWidth="4" />
                    <span>Cargando…</span>
                </div>
            )}

            {!loading && searchedCompany && (
                <>
                    <h2 className="md-section-title new-liquidation__section-title">Profesionales con pendientes</h2>
                    <DataTable
                        value={visibleSummaries}
                        dataKey="professional"
                        selectionMode="checkbox"
                        selection={selected}
                        onSelectionChange={(e) => {
                            setSelected(e.value as PendingProfessionalRow[]);
                            setSelectionError(null);
                        }}
                        expandedRows={expandedRows}
                        onRowToggle={(e) => setExpandedRows(e.data)}
                        rowExpansionTemplate={(row: PendingProfessionalRow) => row.itemCount > 0
                            ? <PendingPreLiquidationItems billingEntityId={searchedCompany.company} professionalId={row.professional} />
                            : (
                                <p className="md-help">
                                    Solo tiene retenciones por facturación propia ({row.retentionLineCount} movimiento(s)).
                                    El detalle se ve en la liquidación del profesional.
                                </p>
                            )}
                        className="crudTable md-selectable"
                        emptyMessage={summaries.length === 0
                            ? 'Esta entidad no tiene pre-liquidaciones ni retenciones pendientes.'
                            : 'No hay resultados para este profesional.'}
                    >
                        <Column selectionMode="multiple" headerStyle={{ width: '48px' }} />
                        <Column expander style={{ width: '48px' }} />
                        <Column field="professionalName" header="Profesional" />
                        <Column field="preLiqCount" header="Pre-liquidaciones" className="text-center" headerClassName="col-center" style={{ width: '150px' }} />
                        <Column field="itemCount" header="Ítems" className="text-center" headerClassName="col-center" style={{ width: '100px' }} />
                        <Column
                            header="Honorario profesional"
                            body={(row: PendingProfessionalRow) => formatMoney(row.professionalAmount)}
                            className="text-right"
                            headerClassName="col-right"
                            style={{ width: '180px' }}
                        />
                        <Column
                            header="Retenciones"
                            headerTooltip="Por facturación propia: lo que se le retiene de las órdenes que facturó él"
                            body={(row: PendingProfessionalRow) => row.retentionTotal !== 0
                                ? <SignedAmount amount={row.retentionTotal} format={formatMoney} />
                                : '—'}
                            className="text-right"
                            headerClassName="col-right"
                            style={{ width: '160px' }}
                        />
                    </DataTable>
                    <FieldError id="new-liquidation-selection-error" message={selectionError} />

                    <div className="new-liquidation__action-bar">
                        <span className="new-liquidation__selection" aria-live="polite">
                            {selected.length === 1 ? '1 profesional seleccionado' : `${selected.length} profesionales seleccionados`}
                            {' · '}
                            <strong>{formatMoney(selectedTotal)}</strong>
                            {!selectedRetentions.eq(0) && <> · Retenciones <strong>{formatMoney(selectedRetentions)}</strong></>}
                        </span>
                        <div className="inputContainer flexible--column new-liquidation__description">
                            <label htmlFor="new-liquidation-description" className="label inputLabel">Descripción</label>
                            <InputText
                                id="new-liquidation-description"
                                value={description}
                                onChange={(e) => setDescription(e.target.value)}
                                placeholder="Ej.: Honorarios de septiembre"
                            />
                        </div>
                        <div className="md-dialog__footer">
                            <Button outlined label="Cancelar" className="md-ghost" onClick={onBack} disabled={creating} />
                            <Button label="Crear" className="md-primary" onClick={handleCreate} loading={creating} />
                        </div>
                    </div>
                </>
            )}
        </div>
    );
}
